import type { Persistence, User as FirebaseAuthUser } from "firebase/auth"

import { AuthenticationContainer } from "@/lib/authentication/container"
import { AuthenticationContext } from "@/lib/authentication/context"
import type { ProcessLocking } from "@/lib/process_locking"

export namespace Authentication {
  export type ContextData = {
    authUser: FirebaseAuthUser | void
    emailAndPassword: {
      forgotPassword: Functions.EmailAndPassword.ForgotPassword
      register: Functions.EmailAndPassword.Register
      signIn: Functions.EmailAndPassword.SignIn
    }
    facebook: {
      authenticate: Functions.Facebook.Authenticate
      register: Functions.Facebook.Register
    }
    fetchPatient: Functions.FetchPatient
    google: {
      authenticate: Functions.Google.Authenticate
      register: Functions.Google.Register
    }
    patient: Patient | void
    providerId: ProviderId | void
    processing: string | void
    signOut: Functions.SignOut
  }

  export type ProviderAuthenticationResult = {
    authenticated: boolean
    cancelled: boolean
    emailAlreadyInUse: boolean
    existingPatient: boolean | void
  }

  export type ProviderId = `google.com` | `password`

  export type RegistrationResult = {
    emailAlreadyInUse: boolean
    registered: boolean
  }

  export type SignUpData = {
    email: string
    password: string
    firstName: string
    lastName: string
    newsletter: boolean
    phoneNumber: string
  }

  export type User = FirebaseAuthUser

  export type UpdateData = Omit<SignUpData, `password`>

  export type Patient = {
    email: string
    firstName: string
    lastName: string
    newsletter?: boolean | null
    phoneNumber?: string | null
    uid: User[`uid`]
  }

  export type SignInData = {
    email: string
    password: string
    persistence: Persistence
  }

  export const Container = AuthenticationContainer
  export const Context = AuthenticationContext

  export namespace Functions {
    export type FetchPatient = ({
      auth_token,
      uid,
    }: {
      auth_token: string
      uid: string
    }) => Promise<Patient | void>

    export namespace EmailAndPassword {
      export type ForgotPassword = ({
        email,
      }: {
        email: string
      }) => Promise<void>

      export type Register = ({
        email,
        firstName,
        lastName,
        newsletter,
        password,
        phoneNumber,
      }: SignUpData) => Promise<ProcessLocking.Conclusion<RegistrationResult>>

      export type SignIn = ({
        email,
        password,
        persistence,
      }: SignInData) => Promise<ProcessLocking.Conclusion<boolean>>
    }

    export namespace Facebook {
      export type Authenticate = () => Promise<void>
      export type Register = () => Promise<void>
    }

    export namespace Google {
      export type Authenticate = () => Promise<
        ProcessLocking.Conclusion<ProviderAuthenticationResult>
      >

      export type Register = ({
        email,
        firstName,
        lastName,
        newsletter,
        phoneNumber,
      }: Omit<SignUpData, `password`>) => Promise<
        ProcessLocking.Conclusion<RegistrationResult>
      >
    }

    export type SignOut = () => Promise<void>

    export type VerifyAuthenticated = () => Promise<boolean>
  }
}
